import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import merge from '../util/merge';
import ItemTable from './ItemTable';
import pubsub from '../util/observer';
import { isObject } from '../util/isType';
import measureScrollbar from '../util/measureScrollbar';
import Loading from '../loading';
import Pagination from '../pagination';
import listenCssRender from '../util/listenCssRender';
import getByteLen from '../util/getByteLen';

class Table extends PureComponent {
  /** *
   *  params:
   *  columns: 表格设定。
   *  title: string类型，标题，非可选
   *  key: string类型，键值，非可选
   *  style: object类型，样式，可选
   *  render: func类型，渲染自定义html，可选
   *  dataSource: array类型，表格渲染的数据，可选。
   *  onClickTh: 函数类型，点击标题触发的回调函数。
   *  loading: bool类型，当图表未加载到数据时候使用，显示正在加载数据状态。
   *  filterData: array类型，表单过滤，可选。
   *  selectSort: array类型，表单排序，可选。
   *  checkboxCallback: func类型，表单选择回调函数，参数：selectDatas, array类型，选择的行数据。
   *  allBorder: bool类型，是否显示全边框，true为显示，false为只显示下边框，可选，默认为false
   *  stripe: bool类型，是否显示条纹表格，true为显示，false为不显示，可选，默认为不显示。
   * */
  static propTypes = {
    /**
     * 加载数据的时候是否显示loading
     * */
    loading: PropTypes.bool,
    /**
     * 表格列设定
     * */
    columns: PropTypes.array,
    /**
     * 普通表格数据源，
     * */
    dataSource: PropTypes.array,
    /**
     * 点击标题回调函数，
     * */
    onClickTh: PropTypes.func,
    /**
     * 自定义类名
     * */
    className: PropTypes.string,
    /**
     * 表格的高度，不设定则为自定义
     * */
    rowHeight: PropTypes.string,
    /**
     * 文字内容是否居中
     * */
    textCenter: PropTypes.bool,
    /**
     * 是否显示选择框
     * */
    checkbox: PropTypes.bool,
    /**
     * 触发选择框的回调函数
     * */
    checkboxCallback: PropTypes.func,
    /**
     * 表格边框设置
     * */
    allBorder: PropTypes.bool,
    /**
     * 表格斑马线设置
     * */
    stripe: PropTypes.bool,
    /**
     * 是否设定hover
     * */
    // hover: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    /**
     * 表格左侧固定栏
     * */
    leftFixed: PropTypes.bool,
    /**
     * 表格右侧固定栏
     * */
    rightFixed: PropTypes.bool,
    /**
     * 是否隐藏表头
     * */
    hideTableTitle: PropTypes.bool,
    /**
     * 自定义样式设定
     * */
    styles: PropTypes.object,
    /**
     * 类名前缀设定
     * */
    prefixCls: PropTypes.string,
    /**
     * 最外层loading层类名设定
     * */
    loadingCls: PropTypes.string,
    /**
     * 显示分页，参数同 Pagination 组件
     * 传 false 不显示分页
     * */
    pagination: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    /**
     * 排序回调函数
     * */
    onSort: PropTypes.func,

    /**
     * 排序回调函数
     * */
    warpBorder: PropTypes.bool,
    /**
     * 表头固定和列固定滚动距离
     * */
    scroll: PropTypes.object,
    /**
     * 展开栏的配置项
     * expandOpt = {
     *      display: true,
     *      icon: <Icon type="xx" />,
     *      render: (data) => {
     *          // data 为当前行数据
     *          return (
     *              <div>展开栏目</div>
     *          )
     *      }
     * }
     * */
    expandOpt: PropTypes.object,
    /**
     * 展开子配置项
     * children = {
     *      display: true,
     *      icon: <Icon type="xx" />,
     *      render: (data) => {
     *          // data 为当前行数据
     *          return (
     *              <div>展开栏目</div>
     *          )
     *      }
     * }
     * */
    children: PropTypes.object,
    /**
     * 当表格为空的时候渲染的函数
     * */
    noDataRender: PropTypes.oneOfType([PropTypes.func, PropTypes.string, PropTypes.element]),
    /**
     * 设置表格的宽度
     * */
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  static defaultProps = {
    onClickTh: () => {},
    // width: '100%',
    className: '',
    checkbox: false,
    checkboxCallback: selectDatas => console.log(selectDatas),
    allBorder: false,
    stripe: false,
    textCenter: false,
    styles: {},
    prefixCls: 'ne-table',
    leftFixed: false,
    rightFixed: false,
    columns: [],
    dataSource: [],
    hideTableTitle: false,
    loading: false,
    pagination: false,
    scroll: {},
    scrollBarWidth: measureScrollbar(),
    noDataRender: '暂无数据',
  };

  constructor(props, context) {
    super(props, context);
    this.pubsubEventString = {
      token: new Date().getTime(),
    };
    const { columns, expandOpt, children, dataSource } = this.props;
    this.dataSource = this.expandDataSourceRow(dataSource, expandOpt || children);
    columns.forEach(item => {
      let width;
      if (!item.width) {
        width = 16 * getByteLen(item.title) * 2;
        if (item.sort) {
          width += 25;
        }
        if (item.filters) {
          width += 25;
        }
        item.width = width;
      }
    });
    this.state = {
      tableConatinerWidth: '100%',
      tdWidthArray: {},
      rowHeightArray: {},
      dataSource: this.dataSource,
      showFixTable: false,
    };
  }

  componentDidMount() {
    /**
     * 监听scroll滚动事件，判断存在固定栏的表格的滚动位置
     * */
    const { leftFixed, rightFixed, prefixCls } = this.props;
    if (leftFixed || rightFixed) {
      let timer = 0;
      const fixBorderDelCls = (currentCls, position) => {
        const shadowCls = `${prefixCls}-${position}-fixed-shadow-border`;
        return currentCls.match(shadowCls) ? currentCls.split(shadowCls)[0] : currentCls;
      };
      const fixBorderAddCls = (currentCls, position) => {
        const shadowCls = `${prefixCls}-${position}-fixed-shadow-border`;
        return currentCls.match(shadowCls) ? currentCls : `${currentCls} ${shadowCls}`;
      };
      this.innerTable.addEventListener(
        'scroll',
        event => {
          const targetNode = event.target;
          const targetScroll = targetNode.getBoundingClientRect().width - 2 + targetNode.scrollLeft;
          const leftCls = this.leftTableItem ? this.leftTableItem.className : null;
          const rightCls = this.rightTableItem ? this.rightTableItem.className : null;
          if (leftFixed) {
            if (targetNode.scrollLeft === 0) {
              this.leftTableItem.className = fixBorderDelCls(leftCls, 'left');
            } else {
              this.leftTableItem.className = fixBorderAddCls(leftCls, 'left');
            }
          }
          if (rightFixed) {
            if (targetScroll + 10 >= this.getScrollTableWidth()) {
              this.rightTableItem.className = fixBorderDelCls(rightCls, 'right');
            } else {
              this.rightTableItem.className = fixBorderAddCls(rightCls, 'right');
            }
          }
          timer && clearTimeout(timer);
          timer = setTimeout(() => {
            if (leftFixed) {
              this.leftTableItem.className = fixBorderDelCls(leftCls, 'left');
            }
            if (rightFixed) {
              this.rightTableItem.className = fixBorderDelCls(rightCls, 'right');
            }
          }, 500);
        },
        false,
      );
    }
    const initInnerTableWidth = () => {
      const { width } = this.innerTable.getBoundingClientRect();
      this.setState({
        tableConatinerWidth: width,
      });
    };
    listenCssRender(initInnerTableWidth);
    listenCssRender(this.handleResizeEvent);
    window.addEventListener('resize', this.handleResizeEvent, false);
  }

  componentWillReceiveProps(nextProps) {
    const { dataSource, stripe, allBorder, warpBorder, expandOpt, children, scroll } = this.props;
    let reRenderWidthHeight = false;
    if (dataSource !== nextProps.dataSource) {
      const { scrollToFirstRowOnChange } = scroll; 
      if (scrollToFirstRowOnChange !== false) this.mainTbody.scrollTop = 0;
      this.setState({
        dataSource: this.expandDataSourceRow(nextProps.dataSource, expandOpt || children),
      });
      this.initFixedColumns();
      reRenderWidthHeight = true;
    }
    if (
      stripe !== nextProps.stripe ||
      allBorder !== nextProps.allBorder ||
      warpBorder !== nextProps.warpBorder
    ) {
      reRenderWidthHeight = true;
    }
    if (reRenderWidthHeight) {
      listenCssRender(this.initItemTableWidthHeight);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResizeEvent, false);
  }

  expandDataSourceRow = (dataSource, expandOpt) => {
    if (expandOpt) {
      dataSource = dataSource.map((item, index) => {
        const tempItem = { ...item };
        if (item._tableExpandRow) {
          tempItem._tableExpandRow = '错误，重复定义展开栏字段';
        } else {
          tempItem._tableExpandRow = () => expandOpt.render(item, index);
        }
        return tempItem;
      });
    }
    return dataSource;
  };

  /**
   * 得到当前鼠标所在位置的表格类型
   * * */
  activeMouse = position => {
    this.activeMouseType = position;
  };

  initItemTableWidthHeight = () => {
    const { leftFixed, rightFixed } = this.props;
    if (leftFixed || rightFixed) {
      const tdWidthArray = {};
      const rowHeightArray = {};
      const getElementChildrenArray = (elm, tag) => [].slice.call(elm.getElementsByTagName(tag));
      const tbodyChildren = getElementChildrenArray(this.mainTbody, 'tr');
      const theadChildren = getElementChildrenArray(this.mainThead, 'th');
      let leftTbodyChildren;
      let leftTheadChildren;
      let rightTbodyChildren;
      let rightTheadChildren;
      if (leftFixed) {
        leftTbodyChildren = getElementChildrenArray(this.leftFixColumnTbody, 'tr');
        leftTheadChildren = getElementChildrenArray(this.leftFixColumnThead, 'th');
      }
      if (rightFixed) {
        rightTbodyChildren = getElementChildrenArray(this.rightFixColumnTbody, 'tr');
        rightTheadChildren = getElementChildrenArray(this.rightFixColumnThead, 'th');
      }

      // 设置均等高度
      tbodyChildren.forEach((item, id) => {
        const { height } = item.getBoundingClientRect();
        rowHeightArray[id] = height;
        if (leftFixed && leftTbodyChildren[id]) {
          leftTbodyChildren[id].style.height = `${height}px`;
        }
        if (rightFixed && rightTbodyChildren[id]) {
          rightTbodyChildren[id].style.height = `${height}px`;
        }
      });
      // 设置均等宽度
      theadChildren.forEach((item, id) => {
        const { width } = item.getBoundingClientRect();
        tdWidthArray[id] = width;
        if (leftFixed && leftTheadChildren[id]) {
          leftTheadChildren[id].style.width = width;
        }
        if (rightFixed && rightTheadChildren[id]) {
          rightTheadChildren[id].style.width = width;
        }
      });
      listenCssRender(
        this.setState({
          baseHeaderHeight: this.mainThead.getBoundingClientRect().height,
        }),
      );
      this.setState({
        tdWidthArray,
        rowHeightArray,
        showFixTable: true,
      });
    }
  };

  handleResizeEvent = debounce(() => {
    this.initItemTableWidthHeight();
    if (this.innerTable) {
      pubsub.publish('handleResizeEvent');
      const { width } = this.innerTable.getBoundingClientRect();
      this.setState({
        tableConatinerWidth: width,
      });
    }
  }, 300);

  /**
   * 初始化三类表格
   * */
  initFixedColumns = () => {
    const { columns, leftFixed, rightFixed, checkbox, expandOpt, children } = this.props;
    const { dataSource } = this.state;
    const fixedProps = position => ({
      position,
      columns: [],
      dataSource: [],
      width: '0px',
      columnsId: [],
    });
    const leftFixedProps = fixedProps('left');
    const rightFixedProps = fixedProps('right');
    const mainProps = {
      columns: merge(columns),
      dataSource: merge(dataSource),
    };
    /**
     * 初始化左侧固定栏和右侧固定栏
     * */
    const initFixedProps = (itemColumns, id) => {
      const type = itemColumns.fixed;
      const renderItemData = originalData => {
        let columnId = id;
        mainProps.columns[id].title = '';
        originalData.columns.push(itemColumns);
        originalData.width = `${parseInt(originalData.width.replace('px', ''), 10) +
          itemColumns.width}px`;
        mainProps.dataSource.forEach((itemData, index) => {
          originalData.dataSource[index] = originalData.dataSource[index] || {};
          if (expandOpt || children) {
            originalData.dataSource[index]._tableExpandRow = dataSource[index]._tableExpandRow;
          }
          Object.assign(originalData.dataSource[index], {
            [itemColumns.key]: itemData[itemColumns.key],
          });
        });
        if (checkbox) {
          columnId += 1;
        }
        if ((expandOpt && expandOpt.display) || (children && children.display)) {
          columnId += 1;
        }
        originalData.columnsId.push(columnId);
      };
      if (type === 'left' && leftFixed) {
        renderItemData(leftFixedProps);
      } else if (type === 'right' && rightFixed) {
        renderItemData(rightFixedProps);
      }
    };
    columns.forEach((item, index) => {
      initFixedProps(item, index);
    });
    return { leftFixedProps, rightFixedProps, mainProps };
  };

  getScrollTableWidth = () => {
    const { columns, checkbox, expandOpt, children } = this.props;
    let tableInnerWidth = 0;
    columns.forEach(item => {
      tableInnerWidth += item.width;
    });
    if (checkbox) {
      tableInnerWidth += 50;
    }
    if ((expandOpt && expandOpt.display) || (children && children.display)) {
      tableInnerWidth += 50;
    }
    return tableInnerWidth;
  };

  handlePassScrollPosition = scrollTop => {
    this.setState({
      scrollTop,
    });
  };

  handleBodyScrollVertical = (elm, type) => {
    const { leftFixed, rightFixed, handleBodyScrollVertical } = this.props;
    const target = elm.target;
    const { scrollTop } = target;
    if (this.activeMouseType === type) {
      if (type !== 'main') {
        this.mainTbody.scrollTop = scrollTop;
      }
      if (leftFixed && type !== 'left') {
        this.leftFixColumnTbody.scrollTop = scrollTop;
      }
      if (rightFixed && type !== 'right') {
        this.rightFixColumnTbody.scrollTop = scrollTop;
      }
    }
    handleBodyScrollVertical && handleBodyScrollVertical(scrollTop);
  };

  render() {
    const {
      onClickTh,
      styles,
      className,
      allBorder,
      stripe,
      textCenter,
      rowHeight,
      prefixCls,
      checkboxCallback,
      checkbox,
      expandOpt,
      children,
      width,
      hideTableTitle,
      leftFixed,
      rightFixed,
      onSort,
      warpBorder,
      pagination,
      scroll,
      loading,
      hover,
      loadingCls,
      scrollBarWidth,
      noDataRender,
    } = this.props;
    const {
      tableConatinerWidth,
      scrollPosition,
      scrollTop,
      rowHeightArray,
      tdWidthArray,
      baseHeaderHeight,
      showFixTable,
    } = this.state;
    let containerCN = `${prefixCls} ${className}`;
    /**
     * 是否具有区分背景
     * */
    if (stripe) {
      containerCN += ' stripe';
    } else {
      containerCN += ` ${prefixCls}-border-row`;
    }

    /**
     * border样式
     * */
    if (warpBorder) {
      containerCN += ` ${prefixCls}-warp-border`;
    }

    if (!warpBorder && allBorder) {
      containerCN += ` ${prefixCls}-all-border`;
    }
    if (textCenter) {
      containerCN += ' text-center';
    }

    const { leftFixedProps, rightFixedProps, mainProps } = this.initFixedColumns();
    const leftStyles = { ...styles, width: leftFixedProps.width };
    const rightStyles = { ...styles, width: rightFixedProps.width };
    const tableInnerWidth = this.getScrollTableWidth();
    const shouldScroll = tableConatinerWidth < tableInnerWidth;
    let warpWidth = null;
    let finalWidth = tableConatinerWidth;
    if (shouldScroll) {
      warpWidth = scrollBarWidth ? tableConatinerWidth - scrollBarWidth : null;
      finalWidth = tableInnerWidth;
    }
    const mainTableProps = {
      hover,
      scroll,
      onSort,
      leftFixed,
      rightFixed,
      onClickTh,
      textCenter,
      rowHeight,
      prefixCls,
      checkbox,
      checkboxCallback,
      expandOpt,
      children,
      hideTableTitle,
      stripe,
      baseHeaderHeight,
      shouldScroll,
      scrollTop,
      scrollBarWidth,
      rowHeightArray,
      tdWidthArray,
      warpBorder,
      allBorder,
      activeMouse: this.activeMouse,
      columns: mainProps.columns,
      dataSource: mainProps.dataSource,
      handlePassScrollPosition: this.handlePassScrollPosition,
      handleBodyScrollVertical: this.handleBodyScrollVertical,
      theadRef: el => {
        this.mainThead = el;
      },
      tbodyRef: el => {
        this.mainTbody = el;
      },
      tableRef: el => {
        this.mainTableItem = el;
      },
      pubsubEventString: this.pubsubEventString,
      handleInnerScrollEvent: elm => (this.innerTable = elm),
      noDataRender,
      styles,
      warpWidth,
      position: 'main',
      width: finalWidth,
    };

    const leftTableProps = {
      hover,
      scroll,
      onSort,
      leftFixed,
      rightFixed,
      onClickTh,
      textCenter,
      rowHeight,
      prefixCls,
      checkbox,
      checkboxCallback,
      expandOpt,
      children,
      hideTableTitle,
      stripe,
      baseHeaderHeight,
      shouldScroll,
      scrollTop,
      scrollBarWidth,
      rowHeightArray,
      tdWidthArray,
      warpBorder,
      allBorder,
      activeMouse: this.activeMouse,
      columns: leftFixedProps.columns,
      dataSource: leftFixedProps.dataSource,
      handlePassScrollPosition: this.handlePassScrollPosition,
      handleBodyScrollVertical: this.handleBodyScrollVertical,
      theadRef: el => {
        this.leftFixColumnThead = el;
      },
      tbodyRef: el => {
        this.leftFixColumnTbody = el;
      },
      tableRef: el => {
        this.leftTableItem = el;
      },
      pubsubEventString: this.pubsubEventString,
      mainTbody: this.mainTbody,
      showFixTable,
      mainDataSource: mainProps.dataSource,
      columnsId: leftFixedProps.columnsId,
      position: leftFixedProps.position,
      styles: leftStyles,
      scrollPosition,
    };

    const rightTableProps = {
      hover,
      scroll,
      onSort,
      position: rightFixedProps.position,
      dataSource: rightFixedProps.dataSource,
      columns: rightFixedProps.columns,
      columnsId: rightFixedProps.columnsId,
      leftFixed,
      rightFixed,
      onClickTh,
      styles: rightStyles,
      textCenter,
      rowHeight,
      prefixCls,
      hideTableTitle,
      stripe,
      width,
      scrollPosition,
      shouldScroll,
      scrollTop,
      scrollBarWidth,
      baseHeaderHeight,
      expandOpt,
      children,
      rowHeightArray,
      tdWidthArray,
      warpBorder,
      allBorder,
      activeMouse: this.activeMouse,
      theadRef: el => {
        this.rightFixColumnThead = el;
      },
      tbodyRef: el => {
        this.rightFixColumnTbody = el;
      },
      tableRef: el => {
        this.rightTableItem = el;
      },
      handlePassScrollPosition: this.handlePassScrollPosition,
      handleBodyScrollVertical: this.handleBodyScrollVertical,
      pubsubEventString: this.pubsubEventString,
      mainTbody: this.mainTbody,
      showFixTable,
      mainDataSource: mainProps.dataSource,
    };
    return (
      <Loading show={loading} className={loadingCls}>
        <div
          className={containerCN}
          style={{ width: width || scroll.x }}
          ref={table => (this.neTable = table)}>
          <ItemTable {...mainTableProps} />
          {leftFixed && <ItemTable {...leftTableProps} />}
          {rightFixed && <ItemTable {...rightTableProps} />}
        </div>
        {isObject(pagination) ? (
          <div className={`${prefixCls}-pagination`}>
            <Pagination isCompact {...pagination} />
          </div>
        ) : null}
      </Loading>
    );
  }
}

export default Table;



// WEBPACK FOOTER //
// ./src/table/Table.jsx